<template>
  <div>
    <div class="md:py-56 py-48 bg-picker border-b">
      <div class="container">
        <div class="flex flex-col items-center">
          <h3 class="mb-24 text-heading md:text-heading-xl">
            {{ $lang('search', 'page_header') }}
          </h3>
          <div class="max-w-556 w-full">
            <TextInput
              class="mb-24"
              :placeholder="$lang('search', 'placeholder')"
              :has-error="status === LoadingStatuses.ERROR"
              has-search-icon
              :is-loading="status === LoadingStatuses.LOADING"
              :init-value="query"
              :is-white-bg="true"
              @submit-input="(e) => query = e"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="container xxl:px-24 md:px-56 px-0 md:py-56 py-48">
      <div>
          <div
            v-if="query.length > 0 && results"
            class="search-results-number text-small-medium md:px-0 px-24 mb-24"
          >
            <div>
              {{ results[0].nbHits + results[1].nbHits }}
              {{ $lang('search', 'hits') }}
            </div>
          </div>
      <div
        v-if="
          results &&
            results[0].nbHits + results[1].nbHits > 0 &&
            query.length > 0
        "
        class="mt-24"
      >
        <div>
          <div v-for="result in results" :key="result.id">
            <div v-if="result.index === productIndex">
              <div v-if="result.hits.length > 0" class="flex flex-wrap w-full">
                <ProductSpot
                  v-for="(product, i) in computedResults"
                  ref="productSpot"
                  :key="`${product.product || i}-main-productlist-spot`"
                  itemprop="itemListElement"
                  :product="
                    new Product({ 
                      ecomData: product, 
                      contentData: null,
                      algoliaObjectID: product.objectID 
                    })
                  "
                  :index="i"
                  :query-i-d="currentQueryID"
                  :is-mostright="(!uiStore.isMobile && (i + 1) % 4 === 0) || (uiStore.isMobile && (i + 1) % 2 === 0 )"
                  :is-mobile="uiStore.isMobile"
                />
              </div>
              <button
                v-if="maxDisplay < results[0].hits.length"
                class="btn btn--primary btn--lg mx-auto mb-32 mt-40"
                @click="maxDisplay += 24"
              >
                {{ $lang('search', 'show_more_hits_button') }}
              </button>
              <button
                v-if="results[0].nbHits > hitsPerPage && maxDisplay >= results[0].hits.length"
                class="btn btn--primary btn--lg mx-auto mb-32 mt-40"
                @click="loadMorePages"
              >
                {{ $lang('search', 'show_more_hits_button') || 'Load More' }}
              </button>
            </div>

            <div v-if="result.index === contentIndex" class="px-24">
              <div v-if="result.hits.length > 0">
                <div class="mb-16 text-small-medium">
                  {{ $lang('search', 'other_hits_heading') }}
                </div>
                <div class="flex md:flex-row flex-col flex-wrap">
                  <div
                    v-for="content in result.hits"
                    :key="content.id"
                    class="product flex md:mb-0 mb-16 mr-16"
                  >
                    <nuxt-link
                      :to="content.permalink"
                      class="text-primary underline text-small-medium"
                    >
                      {{ content.title }}
                    </nuxt-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- No Results Section -->
      <div
        v-if="
          results &&
            results[0].nbHits + results[1].nbHits === 0 &&
            query.length > 0
        "
        class="mt-24 md:px-0 px-24"
      >
        <div class="text-center py-48">
          <h3 class="text-heading mb-16">
            {{ $lang('search', 'no_results_heading') || 'No results found' }}
          </h3>
          <p class="text-small mb-32">
            {{ $lang('search', 'no_results_text') || `We couldn't find any results for "${query}". Try different keywords or browse our popular products below.` }}
          </p>
          
          <!-- Popular Searches as suggestions -->
          <div v-if="popularSearches && popularSearches.words.length > 0" class="mb-32">
            <div class="text-small-medium mb-16">
              {{ $lang('search', 'popular_hits_heading') || 'Popular searches:' }}
            </div>
            <div class="flex flex-wrap justify-center gap-8">
              <button
                v-for="word in popularSearches.words.slice(0, 5)"
                :key="'no-results-popular-' + word"
                class="btn btn--secondary btn--sm"
                @click="query = word"
              >
                {{ word }}
              </button>
            </div>
          </div>
          
          <!-- Trending Products Section (will be populated with Algolia Recommend) -->
          <div class="mt-48">
            <RecommendedProducts
              :recommendations="trendingProducts"
              :heading="$lang('search', 'trending_products') || 'Trending Products'"
              heading-size="medium"
              :is-loading="loadingTrending"
            />
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Ref } from 'vue';
import { useConfigStore } from '~/store/config';
import { useUiStore } from '~/store/ui';
import useAlgoliaSearch from '~/composables/useAlgoliaSearch';
import useAlgoliaRecommend from '~/composables/useAlgoliaRecommend';
import TextInput from '~/components/forms/TextInput.vue';
import ProductSpot from '~/components/productlist/ProductSpot.vue';
import RecommendedProducts from '~/components/generic/RecommendedProducts.vue';
import Product from '~/models/product';
import LoadingStatuses from '~/constants/loading-statuses';
const config = useConfigStore();
const uiStore = useUiStore();
const route = useRoute();
const { algoliaMultipleQueries } = useAlgoliaSearch();
const { getTrendingItems } = useAlgoliaRecommend();

const props = defineProps<{
  page: object,
}>();
/**
 * results: null as any,
 *       query: '',
 *       status: '',
 *       LoadingStatuses,
 *       Product,
 *       maxDisplay: 8,
 */

const status = ref('');
const query = ref<string>(route.query?.s as string || '');
const results = ref<any>(null);
const maxDisplay = ref(24);
const currentPage = ref(0);
const hitsPerPage = 48;
const trendingProducts = ref<any[]>([]);
const loadingTrending = ref(false);
const currentQueryID = ref<string | null>(null);
const { sellerCookieOptions } = useCookieConfig();
const isSeller = useCookie<boolean>('isSeller', { ...sellerCookieOptions, default: () => false }) as Ref<boolean>;

const productIndex = computed(()=> {
  return config.algoliaBaseIndex + '_products';
});
const contentIndex = computed(()=> {
  return config.algoliaBaseIndex + '_content';
});

const popularSearches = computed(()=> {
  return {
    words: config.popularSearches || [],
  };
});

// Build basic filter string
const buildFilters = computed(() => {
  let filters = 'available:true AND NOT var_archived_value:"1"';
  
  const sellerValue = isSeller.value;
  if (sellerValue) {
    filters += ' AND brandName:"Ullmax"';
  }
  
  return filters;
}); 

const search = async() => {
  status.value = LoadingStatuses.LOADING;
  
  const productParams: any = {
    hitsPerPage,
    page: currentPage.value,
    filters: buildFilters.value,
    clickAnalytics: true, // Enable queryID for tracking
  };
  
  const queries = [
    {
      indexName: productIndex.value,
      query: query.value,
      params: productParams,
    },
    {
      indexName: contentIndex.value,
      query: query.value,
      params: {
        hitsPerPage: 8,
      },
    },
  ];

  const res = await algoliaMultipleQueries(queries);
  if (res) {
    results.value = res.results;
    
    // Store queryID for tracking
    if (results.value[0].queryID) {
      currentQueryID.value = results.value[0].queryID;
    }
    
    status.value = LoadingStatuses.SUCCESS;
    
    // Load trending items if no results found
    if (results.value[0].nbHits + results.value[1].nbHits === 0) {
      await loadTrendingProducts();
    }
  } else {
    console.error();
    status.value = LoadingStatuses.ERROR;
  }
};

const loadTrendingProducts = async() => {
  loadingTrending.value = true;
  try {
    const queryParams: any = {
      filters: buildFilters.value,
    };
    
    const trending = await getTrendingItems({
      maxRecommendations: 8,
      queryParameters: queryParams,
    });
    
    trendingProducts.value = trending;
  } catch (error) {
    console.error('Error loading trending products:', error);
  } finally {
    loadingTrending.value = false;
  }
};

const computedResults = computed(() => {
  if (results.value) {
    return maxDisplay.value
      ? results.value[0].hits.slice(0, maxDisplay.value)
      : results.value[0].hits;
  }
  return false;
});

const loadMorePages = async() => {
  currentPage.value++;
  status.value = LoadingStatuses.LOADING;
  
  const productParams: any = {
    hitsPerPage,
    page: currentPage.value,
    filters: buildFilters.value,
    clickAnalytics: true,
  };
  
  const queries = [
    {
      indexName: productIndex.value,
      query: query.value,
      params: productParams,
    },
  ];

  const res = await algoliaMultipleQueries(queries);
  if (res && results.value) {
    // Append new results to existing ones
    results.value[0].hits = [...results.value[0].hits, ...res.results[0].hits];
    status.value = LoadingStatuses.SUCCESS;
  } else {
    status.value = LoadingStatuses.ERROR;
  }
};

watch(query, () => {
  currentPage.value = 0;
  maxDisplay.value = 24;
  search();
});

onMounted(()=> {
  search();
});

</script>

<style scoped lang="postcss">
</style>
